import React, { useState, useEffect, Fragment } from 'react';
import * as PropTypes from 'prop-types';
import cx from 'classnames';
import { CameraIcon } from 'icons/solid';
import { useMedia } from 'use-media';
import { imageTools, sessionstorage } from 'sf-modules';
import { useKeenSlider } from 'keen-slider/react';
import InnerImageZoom from 'react-inner-image-zoom';
import Image from '../../atoms/Image';
import BulletIndicator from '../../atoms/BulletIndicator';
import styles from './ProductImageSlider.module.scss';

const SliderControlBullets = ({ bulletsWrapperClassName, currentSlide, slider, images }) => {
    // @TODO ANA: Improve bullets animation
    const isDesktop = useMedia({ minWidth: 1024 });
    const wrapperClasName = cx(bulletsWrapperClassName, 'dots inline-flex', {
        'w-full': !isDesktop,
    });

    return (
        <div className={wrapperClasName}>
            {images.map((image, idx) => (
                <Fragment key={idx}>
                    {!isDesktop ? (
                        <BulletIndicator
                            type='rect'
                            onClick={() => {
                                slider.moveToSlideRelative(idx);
                            }}
                            className={cx('mr-1 cursor-pointer bg-ui-gray-300 last:mr-0', {
                                '!bg-ui-gray-900': currentSlide === idx,
                            })}
                        />
                    ) : (
                        <div
                            onClick={() => {
                                slider.moveToSlideRelative(idx);
                            }}
                            className='mr-2.5 last:mr-0'
                        >
                            <div
                                className={cx(
                                    'relative h-18 w-18 cursor-pointer overflow-hidden border-2 transition duration-300 ease-out hover:opacity-75',
                                    {
                                        'rounded-sm border-ui-gray-300 hover:border-brand-primary-700':
                                            currentSlide !== idx,
                                        'rounded-full border-brand-primary-700 !bg-ui-gray-900':
                                            currentSlide === idx,
                                    }
                                )}
                            >
                                <Image
                                    layout='fixed'
                                    objectFit='cover'
                                    objectPosition='center'
                                    width={72}
                                    height={72}
                                    src={image.url}
                                />
                            </div>
                        </div>
                    )}
                </Fragment>
            ))}
        </div>
    );
};

const ProductImageSlider = ({ productName, images }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const initialOptions = {
        initial: 0,
        spacing: 2,
        controls: true,
        slideChanged(s) {
            setCurrentSlide(s.details().relativeSlide);
        },
        breakpoints: {
            '(min-width: 1024px)': {
                controls: false,
            },
        },
        resetSlide: true,
    };
    const [sliderRef, slider] = useKeenSlider({ ...initialOptions });

    useEffect(() => {
        if (slider && images) {
            slider.refresh({ ...initialOptions });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slider, images]);

    return (
        <>
            {images && images.length > 0 ? (
                <div className={cx(styles.KeenSliderWrapper, 'relative w-full')}>
                    <div ref={sliderRef} id='productImageSlider' className='keen-slider'>
                        {images.map((image, index) => (
                            <div
                                key={'product-image-slider-' + index}
                                className='keen-slider__slide'
                            >
                                <ImageElement
                                    url={image.url}
                                    alt={image.altText ? image.altText : productName}
                                />
                            </div>
                        ))}
                    </div>

                    {slider && (
                        <SliderControlBullets
                            slider={slider}
                            images={images}
                            currentSlide={currentSlide}
                            bulletsWrapperClassName='absolute lg:relative z-100 lg:mt-6'
                        />
                    )}
                </div>
            ) : (
                <div className='w-full'>
                    <CameraIcon className='mx-auto flex w-3/5 pt-12 pb-32' />
                </div>
            )}
        </>
    );
};

const ImageElement = ({ url, alt }) => {
    if (typeof window === 'undefined') return '';
    const withWebp = sessionstorage.get('webpSupport', false) === 'true';

    return (
        <div className={styles.IizWrapper}>
            <InnerImageZoom
                className='w-full'
                src={imageTools.getOptimizedImageURL(url, {
                    f: withWebp ? 'webp' : 'jpg',
                    w: 688,
                    t: 'crop',
                })}
                zoomSrc={imageTools.getOptimizedImageURL(url, {
                    f: withWebp ? 'webp' : 'jpg',
                    w: 2000,
                    t: 'crop',
                })}
                alt={alt}
            />
        </div>
    );
};

ProductImageSlider.defaultProps = {};

ProductImageSlider.propTypes = {
    productName: PropTypes.string,
    images: PropTypes.array.isRequired,
};

export default React.memo(ProductImageSlider);
